import React, { useEffect, useState, useRef } from "react";

// MUI Components
import Autocomplete from "@mui/material/Autocomplete";
import TextField from "@mui/material/TextField";
import { makeStyles } from "@mui/styles";

// Redux
import { useDispatch } from "react-redux";

// Actions
import { manualRequestFormActions } from "../../store/actions/vorgange/manualRequestFormActions";

const useStyles = makeStyles((theme) => ({
  defaultDropDownCSS: {
    marginTop: "1rem",
    "& .MuiOutlinedInput-root": {
      padding: "2px !important",
      color: theme.palette.MDCColors.color18,
      fontWeight: "initial !important",
    },
    "& .MuiAutocomplete-root": {
      border: `2px solid ${theme.palette.MDCColors.color23}`,
      borderRadius: "5px",
      background: theme.palette.MDCColors.color21,
    },
    "& .MuiOutlinedInput-notchedOutline": {
      border: "none",
    },
  },
  errorText: {
    "& .MuiAutocomplete-root": {
      border: `2px solid ${theme.palette.MDCColors.errorDanger}`,
      background: "rgba(236, 88, 103, 0.3)",
    },
  },
  disabledMenu: {
    "& .MuiAutocomplete-root": {
      border: "0px",
    },
  },
}));

const MDCIBANAutocompleteLookup = React.memo(
  ({
    value,
    onChange,
    error,
    disabled,
    setPreviewHoveredBankOption,
    setPreviewHoveredClassBankOption,
    isClassComponent,
    maxLength,
  }) => {
    const [open, setOpen] = useState(false);
    const [inputValue, setInputValue] = useState(value ?? "");
    const [options, setOptions] = useState([]);
    const [selectedValues, setSelectedValues] = useState([]); // Selected + typed values
    const [loading, setLoading] = useState(false);
    const { defaultDropDownCSS, errorText, disabledMenu } = useStyles();

    const dispatch = useDispatch();

    let MDCStyles = defaultDropDownCSS;
    if (error) {
      MDCStyles = `${MDCStyles} ${errorText}`;
    }

    if (disabled) {
      MDCStyles = `${MDCStyles} ${disabledMenu}`;
    }

    const abortCtrlRef = useRef(null);
    const debounceRef = useRef(null);

    // Fetch Bank Details API results with debounce ---
    useEffect(() => {
      if (debounceRef.current) clearTimeout(debounceRef.current);

      if (inputValue && inputValue.length > 14) {
        debounceRef.current = setTimeout(() => {
          if (abortCtrlRef.current) abortCtrlRef.current.abort();
          const ctrl = new AbortController();
          abortCtrlRef.current = ctrl;
          setLoading(true);

          !disabled &&
            dispatch(
              manualRequestFormActions.getBankDetailsByIban(inputValue, {
                signal: ctrl.signal,
              })
            ).then((res) => {
              setLoading(false);
              const data = res?.data;

              if (Object.keys(data).length > 0 && data.bank && data.bic) {
                setOptions([
                  {
                    label: inputValue,
                    id: data.iban,
                    bic: data.bic,
                    kreditinstitut: data.bank,
                  },
                ]);
              } else {
                setOptions([]);
              }
            });
        }, 300);
      } else {
        setOptions([]);
      }

      return () => {
        if (debounceRef.current) {
          clearTimeout(debounceRef.current);
        }
      };
    }, [inputValue]);

    useEffect(() => {
      return () => {
        if (abortCtrlRef.current) {
          abortCtrlRef.current.abort();
        }
      };
    }, []);

    // --- Handlers ---
    const handleMouseEnter = () => setOpen(true);
    const handleMouseLeave = () => setOpen(false);
    const handleBlur = () => setOpen(false);

    return (
      <div
        className={MDCStyles}
        onMouseEnter={!disabled && handleMouseEnter}
        onMouseLeave={!disabled && handleMouseLeave}
        onBlur={!disabled && handleBlur}
      >
        <Autocomplete
          open={open}
          onOpen={(event) => {
            // Check if all options are disabled
            const allDisabled = options.length < 0;
            if (allDisabled && disabled) {
              event.preventDefault(); // prevent dropdown opening

              setOpen(false);
            } else {
              setOpen(true);
            }
          }}
          onClose={() => setOpen(false)} // Closes dropdown when focus is lost
          freeSolo // allow manual typed entries
          options={options}
          disabled={disabled}
          getOptionLabel={(opt) =>
            typeof opt === "string" ? opt : opt.label ?? ""
          }
          filterOptions={(x) => x} // disable client-side filtering
          loading={loading}
          value={selectedValues}
          inputValue={inputValue}
          onInputChange={(event, newInputValue, reason) => {
            // Keep the text visible even after selecting a custom value
            if (reason === "input" && newInputValue?.length <= maxLength) {
              setInputValue(newInputValue?.toUpperCase() ?? ""); // Convert to uppercase
              onChange(newInputValue === null ? "" : newInputValue?.toUpperCase() ?? "");
            }
          }}
          onChange={(event, newValue, reason) => {
            if (!newValue) {
              setSelectedValues("");
              setInputValue("");
              onChange("");
              return;
            }
            // Handle typed or selected value
            if (
              reason === "selectOption" ||
              reason === "clear" ||
              reason === "blur"
            ) {
              if (typeof newValue === "string") {
                setSelectedValues(newValue);
              } else if (typeof newValue === "object") {
                setSelectedValues(newValue.label ?? "");
                onChange(newValue);
                if (isClassComponent) {
                  setPreviewHoveredClassBankOption(null);
                } else {
                  setPreviewHoveredBankOption(null);
                }
              } else if (!newValue) {
                setSelectedValues("");
                setInputValue("");
                onChange("");
                return;
              }
            }
          }}
          renderOption={(props, option) =>
            !disabled && (
              <li
                {...props}
                key={option.id ?? option.label ?? option}
                onMouseEnter={() => {
                  if (isClassComponent) {
                    setPreviewHoveredClassBankOption(option);
                  } else {
                    setPreviewHoveredBankOption(option);
                  }
                }} // Hover handler
                onMouseLeave={() => {
                  if (isClassComponent) {
                    setPreviewHoveredClassBankOption(null);
                  } else {
                    setPreviewHoveredBankOption(null);
                  }
                }} // Clear on leave
              >
                <div style={{ display: "flex", flexDirection: "column" }}>
                  <strong>IBAN</strong>
                  <span>{option.label ?? option}</span>
                </div>
              </li>
            )
          }
          renderInput={(params) => (
            <TextField
              {...params}
              InputProps={{
                ...params.InputProps,
                endAdornment: <>{params.InputProps.endAdornment}</>,
              }}
            />
          )}
        />
      </div>
    );
  }
);

export default MDCIBANAutocompleteLookup;
